import api from "src/services/api";

export interface OpcionCatalogo {
  codigo: string;
  etiqueta: string;
}

export interface OrdenTrabajoCatalogo {
  se_factura: OpcionCatalogo[];
  trabajos_realizados: OpcionCatalogo[];
  frecuencias: OpcionCatalogo[];
}

export interface PuntoServicioAsignado {
  id: number;
  ps_nombre: string;
  ps_codigo: string;
}

export interface ContextoPS {
  ps: { id: number; codigo: string; nombre: string };
  fiscal: { nombre?: string };
  segmento_operativo?: string;
}

export interface MultiOpcion {
  codigo: string;
  otro?: string | null;
}

/** Detalle de una Orden de Trabajo tal como la devuelve el backend. */
export interface OrdenTrabajoDetalle {
  id: number;
  /** N° de OT visible al usuario (continúa la numeración del sistema anterior). */
  numero?: number | null;
  ps_id: number;
  ps_codigo?: string | null;
  ps_nombre?: string | null;
  fiscal_asignado_nombre?: string | null;
  segmento_operativo?: string | null;
  fecha?: string | null;
  nombre_contacto?: string | null;
  se_factura?: string | null;
  sectores?: string | null;
  conformidad?: boolean | null;
  observaciones?: string | null;
  firma_base64?: string | null;
  estado: "BORRADOR" | "FINALIZADA";
  cliente_correo?: string | null;
  finalizado_en?: string | null;
  creado_por_usuario?: string;
  creado_por_cargo?: string | null;
  creado_en?: string | null;
  actualizado_en?: string | null;
  multi: Record<string, MultiOpcion[]>;
}

/** Payload que envía el front al guardar borrador / finalizar. */
export interface OrdenTrabajoPayload {
  fecha?: string | null;
  nombre_contacto?: string | null;
  se_factura?: string | null;
  sectores?: string | null;
  conformidad?: boolean | null;
  observaciones?: string | null;
  trabajos_realizados: string[];
  frecuencias: string[];
  firma_b64?: string;
}

/** Fila del historial de OT por PS (resumen). */
export interface OrdenTrabajoHistorialItem {
  id: number;
  /** N° de OT visible al usuario (continúa la numeración del sistema anterior). */
  numero?: number | null;
  ps_id: number;
  ps_codigo?: string | null;
  ps_nombre?: string | null;
  fecha?: string | null;
  se_factura?: string | null;
  nombre_contacto?: string | null;
  conformidad?: boolean | null;
  estado: "BORRADOR" | "FINALIZADA";
  creado_en?: string | null;
  finalizado_en?: string | null;
}

/** Respuesta del backend al finalizar una OT (alimenta la pantalla de confirmación). */
export interface OrdenTrabajoFinalizada {
  id: number;
  /** N° de OT visible al usuario. */
  numero?: number | null;
  ps_codigo?: string | null;
  ps_nombre?: string | null;
  finalizado_en?: string | null;
  /** True si el PS tenía correo de contacto, por lo que se envió el detalle. */
  correo_cliente?: boolean;
}

const BASE = "orden-trabajo";

// Lanza un Error con el mensaje real del backend en lugar del genérico de axios.
const lanzarErrorBackend = (error: any): never => {
  const msg =
    error?.response?.data?.error ||
    error?.response?.data?.info ||
    error?.message ||
    "Error de comunicación con el servidor";
  throw new Error(msg);
};

export const obtenerCatalogoOrdenTrabajo = async (): Promise<OrdenTrabajoCatalogo> => {
  try {
    const { data } = await api.get(`${BASE}/catalogo`);
    if (data?.error) throw new Error(data.error);
    return data?.datos as OrdenTrabajoCatalogo;
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};

export const buscarPuntosServicioAsignados = async (q?: string): Promise<PuntoServicioAsignado[]> => {
  try {
    const suffix = q ? `?q=${encodeURIComponent(q)}` : "";
    const { data } = await api.get(`${BASE}/puntos-servicio${suffix}`);
    if (data?.error) throw new Error(data.error);
    return (data?.datos || []) as PuntoServicioAsignado[];
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};

export const obtenerContextoPS = async (psId: number): Promise<ContextoPS> => {
  try {
    const { data } = await api.get(`${BASE}/contexto?ps_id=${psId}`);
    if (data?.error) throw new Error(data.error);
    return data?.datos as ContextoPS;
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};

/** Get-or-create del borrador para el PS seleccionado. Devuelve la OT completa. */
export const iniciarOrdenTrabajo = async (psId: number): Promise<OrdenTrabajoDetalle> => {
  try {
    const { data } = await api.post(`${BASE}/iniciar`, { ps_id: psId });
    if (data?.error) throw new Error(data.error);
    return data?.datos as OrdenTrabajoDetalle;
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};

export const obtenerOrdenTrabajo = async (id: number): Promise<OrdenTrabajoDetalle> => {
  try {
    const { data } = await api.get(`${BASE}/${id}`);
    if (data?.error) throw new Error(data.error);
    return data?.datos as OrdenTrabajoDetalle;
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};

/** Guarda el borrador (campos parciales, sin validación estricta). */
export const guardarBorradorOrdenTrabajo = async (
  id: number,
  payload: OrdenTrabajoPayload
): Promise<void> => {
  try {
    const { data } = await api.put(`${BASE}/${id}`, payload);
    if (data?.error) throw new Error(data.error);
  } catch (error: any) {
    lanzarErrorBackend(error);
  }
};

/** Historial de OT (borradores + finalizadas) de un PS del fiscal logueado. */
export const listarHistorialPorPs = async (
  psId: number
): Promise<OrdenTrabajoHistorialItem[]> => {
  try {
    const { data } = await api.get(`${BASE}/historial?ps_id=${psId}`);
    if (data?.error) throw new Error(data.error);
    return (data?.datos || []) as OrdenTrabajoHistorialItem[];
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};

/** Finaliza la orden (valida obligatorios + firma; dispara el correo al cliente). */
export const finalizarOrdenTrabajo = async (
  id: number,
  payload: OrdenTrabajoPayload
): Promise<OrdenTrabajoFinalizada> => {
  try {
    const { data } = await api.post(`${BASE}/${id}/finalizar`, payload);
    if (data?.error) throw new Error(data.error);
    return (data?.datos || { id }) as OrdenTrabajoFinalizada;
  } catch (error: any) {
    return lanzarErrorBackend(error);
  }
};
